<template>
    <div
        id="search-bar"
        class="relative z-0 flex w-full h-full transition-all duration-500 search-wrapper"
        :class="active ? 'active md:w-80' : 'md:w-40'"
        @blur="deactivate"
    >
        <div
            v-if="active"
            class="fixed top-0 left-0 w-full h-full"
            style="z-index: -2;"
            @click="deactivate"
        ></div>
        <form action="/search" class="flex items-center flex-grow text-blue-500" @click="activate">
            <input
                v-model="query"
                class="flex-grow w-full mx-6 font-medium bg-transparent small"
                placeholder="Search"
                name="query"
                aria-label="Search site"
                @focus="activate"
            />
            <button class="flex-shrink-0 align-middle" aria-label="Submit search query">
                <!-- prettier-ignore -->
                <svg width="15" height="15" viewbox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" focusable="false" aria-label="">
                    <circle cx="6" cy="6" r="5.25" stroke="currentColor" stroke-width="1.5" />
                    <path d="M9.35352 9.64642L13.707 14" stroke="currentColor" stroke-width="1.5" />
                </svg>
            </button>
        </form>
        <transition name="shrink-horizontal">
            <button
                v-if="active"
                aria-label="Close search bar"
                class="w-16 ml-6 bg-blue-500 button"
                @click="deactivate"
                v-on="!query ? { blur: deactivate } : {}"
            >
                <div v-if="loading" class="w-5 h-5 mx-auto text-white loading-spinner"></div>
                <!-- prettier-ignore -->
                <svg v-else class="mx-auto" width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" focusable="false" aria-label="">
                    <path d="M15.6997 13.8891L14.639 14.9498L5.80018 6.11092L6.86084 5.05026L15.6997 13.8891Z" fill="#fff" />
                    <path d="M15.6998 6.11092L6.86099 14.9498L5.80033 13.8891L14.6392 5.05026L15.6998 6.11092Z" fill="#fff" />
                </svg>
            </button>
        </transition>
        <div
            v-if="active && query && query.length > 2"
            class="absolute bottom-0 w-full h-px -mb-px bg-white border-t border-blue-100"
        >
            <a
                v-for="(result, i) in highlightedResults"
                :key="i"
                :href="result.url"
                class="flex flex-col justify-center px-6 py-3 bg-white border-b border-blue-100 md:py-4 small"
            >
                <p class="font-bold text-blue-500 truncate" v-html="result.title"></p>
                <p class="font-normal text-blue-300 truncate" v-html="result.description"></p>
            </a>
            <div class="flex pl-6 text-xs bg-blue-500 md:text-base">
                <div class="flex items-center flex-grow py-6 border-r border-blue-300">
                    <p class="text-blue-300 small">{{ searchMatches.length }} Results</p>
                </div>
                <a
                    class="flex items-center justify-center flex-shrink-0 w-16"
                    :href="'/search?query=' + query"
                    aria-label="Submit search query"
                    @blur="deactivate"
                >
                    <svg
                        fill="#fff"
                        class="w-4 h-4"
                        viewBox="0 0 20 20"
                        role="img"
                        focusable="false"
                        aria-label=""
                    >
                        <path
                            d="M12.95 10.707l0.707-0.707-5.657-5.657-1.414 1.414 4.242 4.243-4.242 4.243 1.414 1.414 4.95-4.95z"
                        ></path>
                    </svg>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    props: {
        urlQuery: {
            type: String,
            default: null
        }
    },

    data: function () {
        return {
            active: !!this.query,
            query: this.urlQuery,
            results: [],
            loading: false,
            debouncer: null
        };
    },

    computed: {
        searchMatches() {
            if (this.query.length < 3) return [];
            const query = this.query.toLowerCase();
            const matches = this.results.filter((result) => {
                const matched =
                    result.title.toLowerCase().includes(query) ||
                    result.description.toLowerCase().includes(query) ||
                    result.searchTags.toLowerCase().includes(query) ||
                    result.searchResultsDescription.toLowerCase().includes(query);
                return matched;
            });
            return matches;
        },

        highlightedResults() {
            return this.searchMatches.slice(0, 5).map((result) => {
                return {
                    title: this.highlightText(result.title),
                    description: this.highlightText(
                        result.searchResultsDescription || result.description
                    ),
                    url: result.url
                };
            });
        }
    },

    // watch: {
    //     query() {
    //         if (this.query.length > 2) {
    //             clearTimeout(this.debouncer);
    //             this.debouncer = setTimeout(this.fetchSearchResults, 500);
    //         }
    //     }
    // },

    created() {
        this.fetchData();
    },

    methods: {
        activate() {
            this.active = true;
            document.body.classList.add('search-active');
        },
        deactivate() {
            this.active = false;
            this.query = '';
            document.body.classList.remove('search-active');
        },
        fetchData() {
            this.loading = true;
            if (!window.searchData) {
                window.searchData = axios.get(`/marketing-api/search-data.json`);
            }
            window.searchData.then((response) => {
                this.results = response.data.data;
            });
            this.loading = false;
        },
        highlightText(text) {
            if (!text) return '';
            const pattern = new RegExp(this.query, 'gi');
            return text.replace(
                pattern,
                `<span class='px-1 bg-yellow-500 rounded'>${this.query}</span>`
            );
        }
    }
};
</script>

<style>
/* purgecss start ignore */

.search-wrapper form > input::placeholder {
    @apply text-blue-500 opacity-100;
}

.search-wrapper .button {
    max-width: 90px;
}

.search-wrapper:before {
    content: '';
    @apply absolute bg-white w-full h-full opacity-0 z-negative transition-opacity duration-500;
}

.search-wrapper.active:before {
    @apply opacity-100;
}

/* purgecss end ignore */
</style>
